import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound, CheckCircle } from "lucide-react";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // Store token but don't redeem until submit
  const [tokenHash, setTokenHash] = useState<string | null>(null);
  const [tokenType, setTokenType] = useState<string | null>(null);
  const [hasSession, setHasSession] = useState(false);

  useEffect(() => {
    // Tracks whether the link has already been judged, so the safety timeout
    // below never overwrites a good result with an error message.
    let settled = false;
    const settle = () => {
      settled = true;
      setIsCheckingSession(false);
    };

    // Check for error in URL hash
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const errorCode = hashParams.get('error_code');
    const errorDesc = hashParams.get('error_description');

    if (errorCode === 'otp_expired' || errorDesc?.includes('expired')) {
      setErrorMessage('This reset link has expired. Please request a new one.');
      settle();
      return;
    }


    // Check for token in query params
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get('token_hash') || searchParams.get('token');
    const type = searchParams.get('type');
    const code = searchParams.get('code');

    // PKCE flow: exchange code for session (used on mobile)
    if (code) {
      const exchangeCode = async () => {
        try {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            console.error('Code exchange error:', error);
            setErrorMessage('This reset link has expired or is invalid. Please request a new one.');
          } else {
            setHasSession(true);
            setIsReady(true);
          }
        } catch (err) {
          console.error('Code exchange failed:', err);
          setErrorMessage('An error occurred. Please try again.');
        } finally {
          settle();
        }
      };
      exchangeCode();
      return;
    }

    if (token && type === 'recovery') {
      // Store token but don't redeem yet
      setTokenHash(token);
      setTokenType(type);
      setIsReady(true);
      settle();
      return;
    }

    // Check for existing session
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          setHasSession(true);
          setIsReady(true);
        } else {
          // Legacy implicit flow: tokens in hash
          const accessToken = hashParams.get('access_token');
          const hashType = hashParams.get('type');

          if (accessToken && hashType === 'recovery') {
            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: hashParams.get('refresh_token') || '',
            });

            if (!error) {
              setHasSession(true);
              setIsReady(true);
            } else {
              setErrorMessage('This link is invalid or has expired.');
            }
          } else if (!settled) {
            setErrorMessage('Invalid password reset link. Please request a new one.');
          }
        }
      } catch (err) {
        console.error("Error checking session:", err);
        setErrorMessage('An error occurred. Please try again.');
      } finally {
        settle();
      }
    };

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        // A late arriving session means the link was good after all.
        setErrorMessage(null);
        setHasSession(true);
        setIsReady(true);
        settle();
      }
    });

    checkSession();

    // Add timeout to prevent infinite loading on mobile
    const timeout = setTimeout(() => {
      if (!settled) {
        setErrorMessage('Verification is taking too long. Please try the link again or request a new one.');
        settle();
      }
    }, 15000); // 15 second timeout

    return () => {
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []);


  const validatePassword = (pwd: string) => {
    const minLength = pwd.length >= 8;
    const hasUppercase = /[A-Z]/.test(pwd);
    const hasLowercase = /[a-z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    return { minLength, hasUppercase, hasLowercase, hasNumber, isValid: minLength && hasUppercase && hasLowercase && hasNumber };
  };

  const passwordValidation = validatePassword(password);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!passwordValidation.isValid) {
      toast.error("Please meet all password requirements");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setIsLoading(true);
    
    try {
      if (tokenHash && tokenType) {
        // Redeem token NOW (on submit, not on page load)
        const { error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: 'recovery',
        } as any);

        if (verifyError) {
          throw new Error('This link has expired. Please request a new password reset.');
        }
      }

      // Now set the password
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;

      toast.success("Password reset successfully!");
      navigate("/login");
      
    } catch (error: any) {
      console.error("Error resetting password:", error);
      toast.error(error.message || "Failed to reset password");
    } finally {
      setIsLoading(false);
    }
  };

  if (isCheckingSession) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-background to-muted p-4">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mb-4" />
        <div className="text-muted-foreground text-center">
          Verifying your reset link...
        </div>
        <p className="text-sm text-muted-foreground mt-2">This should only take a moment</p>
      </div>
    );
  }

  if (errorMessage || !isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <KeyRound className="h-12 w-12 mx-auto text-destructive mb-4" />
            <CardTitle>Invalid or Expired Link</CardTitle>
            <CardDescription>
              {errorMessage || 'This password reset link is invalid or has expired. Please request a new one.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button 
              className="w-full" 
              onClick={() => navigate("/forgot-password")}
            >
              Request New Reset Link
            </Button>
            <Button 
              variant="outline"
              className="w-full" 
              onClick={() => navigate("/login")}
            >
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
            <KeyRound className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Reset Your Password</CardTitle>
          <CardDescription>
            Enter your new password below.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your new password"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* Password requirements */}
            <div className="text-sm space-y-1">
              <div className={`flex items-center gap-2 ${passwordValidation.minLength ? 'text-green-600' : 'text-muted-foreground'}`}>
                <CheckCircle className={`h-4 w-4 ${passwordValidation.minLength ? 'opacity-100' : 'opacity-30'}`} />
                At least 8 characters
              </div>
              <div className={`flex items-center gap-2 ${passwordValidation.hasUppercase ? 'text-green-600' : 'text-muted-foreground'}`}>
                <CheckCircle className={`h-4 w-4 ${passwordValidation.hasUppercase ? 'opacity-100' : 'opacity-30'}`} />
                One uppercase letter
              </div>
              <div className={`flex items-center gap-2 ${passwordValidation.hasLowercase ? 'text-green-600' : 'text-muted-foreground'}`}>
                <CheckCircle className={`h-4 w-4 ${passwordValidation.hasLowercase ? 'opacity-100' : 'opacity-30'}`} />
                One lowercase letter
              </div>
              <div className={`flex items-center gap-2 ${passwordValidation.hasNumber ? 'text-green-600' : 'text-muted-foreground'}`}>
                <CheckCircle className={`h-4 w-4 ${passwordValidation.hasNumber ? 'opacity-100' : 'opacity-30'}`} />
                One number
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="text-sm text-destructive">Passwords do not match</p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading || !passwordValidation.isValid || password !== confirmPassword}
            >
              {isLoading ? "Resetting Password..." : "Reset Password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
